
(() => {
  const ROOT_SELECTOR = '[data-unbegun-gift-wrap]';
  const SERVICE_HANDLE = 'unbegun-personalizer';
  let reconciling = false;

  // Customer-facing properties only. Internal link keys remain unchanged.
  const PROPERTY_LABELS = {
    nl: { gift: 'Cadeauverpakking', message: 'Persoonlijke boodschap', product: 'Bij product', yes: 'Ja' },
    en: { gift: 'Gift wrapping', message: 'Personal message', product: 'For product', yes: 'Yes' },
    de: { gift: 'Geschenkverpackung', message: 'Persönliche Nachricht', product: 'Für Produkt', yes: 'Ja' },
    fr: { gift: 'Emballage cadeau', message: 'Message personnel', product: 'Pour le produit', yes: 'Oui' }
  };

  function propertyLabels() {
    const lang = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    return PROPERTY_LABELS[lang] || PROPERTY_LABELS.nl;
  }

  function existingPropertyKey(properties, kind) {
    return Object.values(PROPERTY_LABELS)
      .map(labels => labels[kind])
      .find(key => Object.prototype.hasOwnProperty.call(properties || {}, key));
  }

  const shopRoot = () => window.Shopify?.routes?.root || '/';

  function money(cents, format) {
    const amount = Number(cents || 0);
    const comma = (amount / 100).toFixed(2).replace('.', ',');
    const dot = (amount / 100).toFixed(2);

    return String(format || '€{{ amount_with_comma_separator }}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount\s*\}\}/g, dot);
  }

  function selectionForForm(form) {
    const root = form?.querySelector(ROOT_SELECTOR);

    if (!root) {
      return { enabled: false };
    }

    const enabled = Boolean(
      root.querySelector('[data-gift-wrap-toggle]')?.checked
    );

    return {
      enabled,
      variantId: Number(root.dataset.giftVariantId || 0),
      price: Number(root.dataset.giftPrice || 195),
      message: String(
        root.querySelector('[data-gift-wrap-message]')?.value || ''
      ).trim(),
      productTitle: root.dataset.productTitle || ''
    };
  }

  function appendGiftToPayload(payload, form, quantity, giftLink) {
    const gift = selectionForForm(form);

    if (!gift.enabled || !gift.variantId || !payload?.items?.length) {
      return gift;
    }

    const base = payload.items[0];
    const labels = propertyLabels();

    base.properties = Object.assign({}, base.properties || {}, {
      [labels.gift]: labels.yes,
      ...(gift.message
        ? { [labels.message]: gift.message }
        : {}),
      '_Cadeau koppeling': giftLink,
      '_Cadeau variant': String(gift.variantId)
    });

    payload.items.push({
      id: gift.variantId,
      quantity,
      properties: {
        [labels.product]: gift.productTitle,
        ...(gift.message
          ? { [labels.message]: gift.message }
          : {}),
        '_Cadeau koppeling': giftLink,
        '_Unbegun service': 'cadeauverpakking'
      }
    });

    return gift;
  }

  async function reconcile() {
    if (reconciling) {
      return false;
    }

    reconciling = true;

    try {
      const response = await fetch(
        `${shopRoot()}cart.js?_=${Date.now()}`,
        {
          cache: 'no-store',
          credentials: 'same-origin'
        }
      );

      if (!response.ok) {
        return false;
      }

      const cart = await response.json();
      const desired = new Map();
      const services = new Map();
      const bases = new Map();

      cart.items.forEach((item) => {
        const baseLink = item.properties?.['_Cadeau koppeling'];

        if (!baseLink) {
          return;
        }

        const isService =
          item.handle === SERVICE_HANDLE &&
          item.properties?.['_Unbegun service'] === 'cadeauverpakking';

        if (isService) {
          if (!services.has(baseLink)) {
            services.set(baseLink, []);
          }

          services.get(baseLink).push(item);
        } else {
          desired.set(
            baseLink,
            (desired.get(baseLink) || 0) + Number(item.quantity || 0)
          );

          if (!bases.has(baseLink)) {
            bases.set(baseLink, item);
          }
        }
      });

      const updates = {};
      const additions = [];

      services.forEach((items, link) => {
        const qty = desired.get(link) || 0;

        items.forEach((item, index) => {
          const wanted = index === 0 ? qty : 0;

          if (Number(item.quantity || 0) !== wanted) {
            updates[item.key] = wanted;
          }
        });
      });

      desired.forEach((qty, link) => {
        if (!qty || services.has(link)) {
          return;
        }

        const base = bases.get(link);
        const variantId = Number(
          base?.properties?.['_Cadeau variant'] || 0
        );

        if (!variantId) {
          return;
        }

        const baseProperties = base?.properties || {};
        const labels = propertyLabels();
        // Preserve the original language of an existing cart item on recovery.
        const messageKey = existingPropertyKey(baseProperties, 'message') || labels.message;
        const giftKey = existingPropertyKey(baseProperties, 'gift');
        const sourceLabels = Object.values(PROPERTY_LABELS).find(
          item => item.gift === giftKey || item.message === messageKey
        ) || labels;
        const message = String(baseProperties[messageKey] || '');

        additions.push({
          id: variantId,
          quantity: qty,
          properties: {
            [sourceLabels.product]:
              base?.product_title || base?.title || 'Product',
            ...(message
              ? { [messageKey]: message }
              : {}),
            '_Cadeau koppeling': link,
            '_Unbegun service': 'cadeauverpakking'
          }
        });
      });

      let changed = false;

      if (Object.keys(updates).length) {
        const update = await fetch(`${shopRoot()}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates })
        });

        changed = update.ok || changed;
      }

      if (additions.length) {
        const add = await fetch(`${shopRoot()}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: additions })
        });

        changed = add.ok || changed;
      }

      return changed;
    } catch (error) {
      console.error('[UNBEGUN Gift Wrap] Reconcile failed', error);
      return false;
    } finally {
      reconciling = false;
    }
  }

  function updateExtraOptions(form) {
  const summary = form?.querySelector(
    '[data-unbegun-extra-options]'
  );

  if (!summary) {
    return;
  }

  const keyboardModule = form.querySelector(
    '[data-unbegun-ipad-keyboard]'
  );

  if (keyboardModule && summary.previousElementSibling !== keyboardModule) {
    keyboardModule.insertAdjacentElement('afterend', summary);
  }

    const gift = selectionForForm(form);
    const personalizer = form.querySelector(
      '[data-unbegun-personalizer]'
    );

    const personalizationEnabled = Boolean(
      personalizer
        ?.querySelector('[data-personalizer-toggle]')
        ?.checked
    );

    const initials = String(
      personalizer
        ?.querySelector('[data-personalizer-initials]')
        ?.value || ''
    ).trim();

    const personalizationPrice = personalizationEnabled
      ? Number(
          initials.length === 2
            ? personalizer.dataset.twoLetterPrice
            : personalizer.dataset.oneLetterPrice
        )
      : 0;

    const giftPrice = gift.enabled ? gift.price : 0;

    const keyboard =
      window.UnbegunIpadKeyboard?.getSelection?.(form) || {
        enabled: false,
        price: 0
      };

    const keyboardPrice = keyboard.enabled
      ? Number(keyboard.price || 0)
      : 0;

    const total =
      personalizationPrice + giftPrice + keyboardPrice;

    const format =
      summary
        .closest('form')
        ?.querySelector(ROOT_SELECTOR)
        ?.dataset.moneyFormat ||
      '€{{ amount_with_comma_separator }}';

    const pRow = summary.querySelector(
      '[data-extra-personalization]'
    );

    const gRow = summary.querySelector(
      '[data-extra-gift-wrap]'
    );

    const kRow = summary.querySelector(
      '[data-extra-ipad-keyboard]'
    );

    const totalRow = summary.querySelector(
      '[data-extra-total-row]'
    );

    pRow.hidden = !personalizationEnabled;
    gRow.hidden = !gift.enabled;

    if (kRow) {
      kRow.hidden = !keyboard.enabled;
    }

    if (personalizationEnabled) {
      summary.querySelector(
        '[data-extra-personalization-price]'
      ).textContent = money(personalizationPrice, format);
    }

    if (gift.enabled) {
      summary.querySelector(
        '[data-extra-gift-wrap-price]'
      ).textContent = money(giftPrice, format);
    }

    if (keyboard.enabled && kRow) {
      summary.querySelector(
        '[data-extra-ipad-keyboard-price]'
      ).textContent = money(keyboardPrice, format);
    }

    const selectedCount = [
      personalizationEnabled,
      gift.enabled,
      keyboard.enabled
    ].filter(Boolean).length;

    totalRow.hidden = selectedCount < 2;

    if (!totalRow.hidden) {
      summary.querySelector(
        '[data-extra-total]'
      ).textContent = money(total, format);
    }

    summary.hidden = total === 0;
  }

  async function addGiftOnly(event, root, form) {
    const gift = selectionForForm(form);

    if (!gift.enabled) {
      return;
    }

    const personalizationOn = Boolean(
      form
        .querySelector('[data-personalizer-toggle]')
        ?.checked
    );

    if (personalizationOn) {
      return;
    }

    if (
      window.UnbegunIpadKeyboard?.validate &&
      !window.UnbegunIpadKeyboard.validate(form, {
        focus: true
      })
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const variantId = Number(
      form.querySelector('[name="id"]')?.value || 0
    );

    const quantity = Math.max(
      1,
      Number(
        form.querySelector('[name="quantity"]')?.value || 1
      )
    );

    if (!variantId || !gift.variantId) {
      return;
    }

    const link =
      `UG-${Date.now()}-` +
      Math.random()
        .toString(36)
        .slice(2, 8)
        .toUpperCase();

    const payload = {
      items: [
        {
          id: variantId,
          quantity,
          properties: {}
        }
      ]
    };

    appendGiftToPayload(
      payload,
      form,
      quantity,
      link
    );

    const keyboardSelection =
      window.UnbegunIpadKeyboard?.getSelection?.(form);

    if (keyboardSelection?.enabled) {
      const keyboardLink =
        `UK-${Date.now()}-` +
        Math.random()
          .toString(36)
          .slice(2, 8)
          .toUpperCase();

      window.UnbegunIpadKeyboard.appendToPayload(
        payload,
        form,
        quantity,
        keyboardLink
      );
    }

    window.UnbegunPreorder?.appendToPayload?.(
      payload,
      form
    );

    const button = form.querySelector(
      '[data-add-button]'
    );

    button?.setAttribute('disabled', 'disabled');

    try {
      const response = await fetch(
        `${shopRoot()}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.description ||
            'Toevoegen aan winkelwagen is niet gelukt.'
        );
      }

      if (window.UnbegunCart?.openAfterMutation) {
        await window.UnbegunCart.openAfterMutation();
      } else if (window.UnbegunCart?.open) {
        await window.UnbegunCart.open();
      } else {
        window.location.assign(`${shopRoot()}cart`);
      }
    } catch (error) {
      console.error('[UNBEGUN Gift Wrap]', error);
    } finally {
      button?.removeAttribute('disabled');
    }
  }

  function init(root) {
    if (root.dataset.giftWrapReady === 'true') {
      return;
    }

    const form = root.closest(
      'form[data-product-form]'
    );

    if (!form) {
      return;
    }

    root.dataset.giftWrapReady = 'true';

    const toggle = root.querySelector(
      '[data-gift-wrap-toggle]'
    );

    const content = root.querySelector(
      '[data-gift-wrap-content]'
    );

    const message = root.querySelector(
      '[data-gift-wrap-message]'
    );

    const counter = root.querySelector(
      '[data-gift-wrap-counter]'
    );

    const button = form.querySelector(
      '[data-add-button]'
    );

    const render = () => {
      content.hidden = !toggle.checked;
      root.classList.toggle(
        'is-open',
        toggle.checked
      );

      if (counter) {
        counter.textContent =
          `${message.value.length}/250`;
      }

      updateExtraOptions(form);
    };

    toggle.addEventListener('change', render);
    message.addEventListener('input', render);

    button?.addEventListener(
      'click',
      (event) => addGiftOnly(event, root, form),
      true
    );

    form.addEventListener(
      'submit',
      (event) => addGiftOnly(event, root, form),
      true
    );

    form.addEventListener(
      'input',
      () => updateExtraOptions(form)
    );

    form.addEventListener(
      'change',
      () => updateExtraOptions(form)
    );

    render();
  }

  function boot() {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    boot
  );

  window.UnbegunGiftWrap = {
    getSelection: selectionForForm,
    appendToPayload: appendGiftToPayload,
    reconcile,
    updateSummary: updateExtraOptions
  };
})();

/*
 * UNBEGUN — Custom Size extra-options summary
 * Adds the €7.50 made-to-measure surcharge to the PDP summary.
 * Does not modify cart or checkout logic.
 */
(() => {
  'use strict';

  const CUSTOM_PRICE = 750;

  const labels = {
    nl: 'Op maat gemaakt',
    en: 'Made to measure',
    de: 'Maßanfertigung',
    fr: 'Sur mesure'
  };

  function language() {
    const lang = (document.documentElement.lang || 'nl')
      .slice(0, 2)
      .toLowerCase();

    return labels[lang] ? lang : 'nl';
  }

  function priceInCents(text) {
    const value = String(text || '')
      .replace(/[^\d,.-]/g, '')
      .trim();

    if (!value) return 0;

    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');
    const separator = Math.max(lastComma, lastDot);

    if (separator === -1) {
      return Math.round(Number(value) * 100) || 0;
    }

    const whole = value.slice(0, separator).replace(/\D/g, '');
    const decimals = value.slice(separator + 1).replace(/\D/g, '');

    return (Number(whole) || 0) * 100 +
      Number((decimals + '00').slice(0, 2));
  }

  function formatPrice(cents) {
  const lang = (document.documentElement.lang || 'nl').toLowerCase();
  const locale = lang.startsWith('en') ? 'en-IE' : 'nl-NL';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR'
  })
    .format(cents / 100)
    .replace(/[\s\u00A0\u202F]+/g, '');
}

  function updateSummary() {
    document.querySelectorAll(
      '[data-unbegun-extra-options]'
    ).forEach(summary => {
      const form = summary.closest('form');
      if (!form) return;

      const customModule =
        form.querySelector('[data-unbegun-custom-size]') ||
        form.closest(
          '.product__details, .product__meta, .product__info, [data-product-section]'
        )?.querySelector('[data-unbegun-custom-size]') ||
        document.querySelector('[data-unbegun-custom-size]');

      if (!customModule) return;

      const customEnabled = Boolean(
        customModule.querySelector(
          '[data-custom-size-toggle]'
        )?.checked
      );

      let customRow = summary.querySelector(
        '[data-extra-custom-size]'
      );

      if (!customRow) {
        customRow = document.createElement('div');
        customRow.className = 'unbegun-extra-options__row';
        customRow.setAttribute('data-extra-custom-size', '');
        customRow.hidden = true;

        const label = document.createElement('span');
        label.textContent = labels[language()];

        const price = document.createElement('strong');
        price.setAttribute('data-extra-custom-size-price', '');
        price.textContent = formatPrice(CUSTOM_PRICE);

        customRow.append(label, price);

        const totalRow = summary.querySelector(
          '[data-extra-total-row]'
        );

        if (totalRow) {
          totalRow.before(customRow);
        } else {
          summary.querySelector(
            '.unbegun-extra-options__rows'
          )?.appendChild(customRow);
        }
      }

      customRow.hidden = !customEnabled;

      const options = [
        '[data-extra-personalization]',
        '[data-extra-gift-wrap]',
        '[data-extra-ipad-keyboard]',
        '[data-extra-custom-size]'
      ];

      let total = 0;
      let selectedCount = 0;

      options.forEach(selector => {
        const row = summary.querySelector(selector);

        if (!row || row.hidden) return;

        selectedCount += 1;

        const price = row.querySelector('strong');
        total += priceInCents(price?.textContent);
      });

      const totalRow = summary.querySelector(
        '[data-extra-total-row]'
      );

      if (totalRow) {
        totalRow.hidden = selectedCount < 2;

        const totalPrice = totalRow.querySelector(
          '[data-extra-total]'
        );

        if (totalPrice) {
          totalPrice.textContent = formatPrice(total);
        }
      }

      summary.hidden = selectedCount === 0;
    });
  }

  function scheduleUpdate() {
    // Run after the existing gift-wrap summary has updated.
    window.setTimeout(updateSummary, 0);
  }

  document.addEventListener(
    'change',
    scheduleUpdate
  );

  document.addEventListener(
    'input',
    scheduleUpdate
  );

  document.addEventListener(
    'shopify:section:load',
    scheduleUpdate
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      scheduleUpdate
    );
  } else {
    scheduleUpdate();
  }
})();